import ContactForm from "../components/ContactForm";
import PageHeader from "../components/PageHeader";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";

export default function ContactUs() {
  const brand =
    "from-blue-600 via-cyan-500 to-blue-700 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900";

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Contact Us | UAE Discounted Hotels</title>

        <meta
          name="description"
          content="Contact UAE Discounted Hotels for hotel booking assistance, partnership inquiries, customer support, and the latest discounted hotel deals across the UAE."
        />

        <meta
          name="keywords"
          content="Contact UAE Discounted Hotels, hotel booking support UAE, customer service, Dubai hotel booking, Abu Dhabi hotels, UAE hotel deals, hotel inquiries"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="UAE Discounted Hotels" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://uaediscountedhotels.com/contact-us"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="UAE Discounted Hotels" />
        <meta property="og:locale" content="en_US" />

        <meta
          property="og:title"
          content="Contact Us | UAE Discounted Hotels"
        />

        <meta
          property="og:description"
          content="Get in touch with UAE Discounted Hotels for hotel reservations, support, partnership opportunities, and travel-related inquiries."
        />

        <meta
          property="og:url"
          content="https://uaediscountedhotels.com/contact-us"
        />

        <meta
          property="og:image"
          content="https://uaediscountedhotels.com/images/default-og.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="Contact Us | UAE Discounted Hotels"
        />

        <meta
          name="twitter:description"
          content="Reach out to UAE Discounted Hotels for hotel booking support, customer service, and inquiries about the best hotel deals across the UAE."
        />

        <meta
          name="twitter:image"
          content="https://uaediscountedhotels.com/images/default-og.png"
        />
      </Helmet>

      <div className="w-full bg-gray-50 dark:bg-gray-950 transition">

        {/* HEADER */}
        <PageHeader
          title="Contact Us"
          subtitle="We’re always here to assist your travel needs"
        />

        {/* MAIN SECTION */}
        <section className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* LEFT SIDE */}
          <div className="lg:col-span-1 space-y-5">

            {[
              {
                icon: <FaMapMarkerAlt />,
                title: "Office",
                value: "Dubai, UAE",
              },
              {
                icon: <FaPhoneAlt />,
                title: "Phone",
                value: "+971 123 456 789",
              },
              {
                icon: <FaEnvelope />,
                title: "Email",
                value: "info@southtravels.com",
              },
              {
                icon: <FaClock />,
                title: "Working Hours",
                value: "24/7 Support",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="
                  flex items-center gap-4
                  bg-white dark:bg-gray-900
                  border border-gray-200 dark:border-gray-800
                  rounded-2xl p-5
                  shadow-sm hover:shadow-lg
                  transition
                "
              >
                <div
                  className={`
                    w-12 h-12 flex items-center justify-center
                    rounded-xl text-white
                    bg-gradient-to-r ${brand}
                  `}
                >
                  {item.icon}
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {item.title}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {item.value}
                  </p>
                </div>
              </div>
            ))}

            {/* MAP */}
            <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-800 shadow-md">
              <iframe
                src="https://www.google.com/maps?q=Dubai&output=embed"
                className="w-full h-64"
                loading="lazy"
              />
            </div>
          </div>

          {/* RIGHT SIDE FORM */}
          <div className="lg:col-span-2">

            <div className="
              w-full
              bg-white dark:bg-gray-900
              border border-gray-200 dark:border-gray-800
              rounded-3xl
              shadow-[0_20px_70px_rgba(0,0,0,0.12)]
              dark:shadow-[0_20px_70px_rgba(0,0,0,0.55)]
              p-6 sm:p-10
              relative overflow-hidden
            ">

              {/* decorative glow */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl"></div>

              {/* LETS TALK HEADER (TOP OF FORM) */}
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Let’s Talk 👋
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mt-2">
                  Fill out the form and our team will get back within 24 hours
                </p>
              </div>

              {/* FULL WIDTH FORM */}
              <div className="w-full">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-7xl mx-auto px-4 pb-16">
          <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-3">
            {[
              {
                q: "How fast do you respond?",
                a: "Usually within 24 hours.",
              },
              {
                q: "Do you offer custom travel plans?",
                a: "Yes, we create fully customized travel experiences.",
              },
              {
                q: "Is support available 24/7?",
                a: "Yes, our team is available around the clock.",
              },
            ].map((faq, i) => (
              <details
                key={i}
                className="
                  bg-white dark:bg-gray-900
                  border border-gray-200 dark:border-gray-800
                  rounded-xl p-4
                  shadow-sm hover:shadow-md transition
                "
              >
                <summary className="cursor-pointer font-semibold text-gray-900 dark:text-white">
                  {faq.q}
                </summary>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </section>

      </div>
    </>
  );
}